import { z } from "zod";
import { worksites } from "../../attendances";
import { localized } from "../../locales";
import type { Enums } from "../../types";

/**
 * Ties an attendance's project to its worksite.
 *
 * A project belongs to a project site and nowhere else: `project-site` asks for one, every other
 * worksite refuses one. The worksite is optional here because the check-in body leaves it out on a
 * check-out — without a worksite there is nothing for a project to belong to.
 *
 * A schema that carries this refinement cannot be narrowed with `.partial()`, `.omit()`, `.pick()`
 * or `.merge()` — zod 4 throws at import time; build a second schema (as the check-in body and the
 * requirements query do) instead of deriving one.
 *
 * @param values - The worksite and project of the value under validation.
 * @param context - The refinement context the issues are reported to.
 */
export function attendanceWorksiteRefine(
	values: { worksite?: Enums<"attendanceWorksiteType">; projectId?: string },
	context: z.core.$RefinementCtx,
): void {
	if (values.worksite === "project-site" && !values.projectId) {
		context.addIssue({
			code: "custom",
			message: localized("projects.select_project"),
			path: ["projectId"],
		});
	}

	if (values.projectId && values.worksite !== "project-site") {
		context.addIssue({
			code: "custom",
			message: 'worksite must be "project-site" when projectId is assigned',
			path: ["worksite"],
		});
	}
}

export const attendanceFormSchema = z
	.object({
		worksite: z.enum(worksites),
		projectId: z.uuid().optional(),
	})
	.superRefine(attendanceWorksiteRefine);

export type AttendanceFormSchema = z.infer<typeof attendanceFormSchema>;
